import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, Divider, Stack, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import TextInput from 'components/Common/TextInput';
import Button from 'components/Common/CustomButton';
import Selector from 'components/Common/Selector';
import Modal from 'components/Common/Modal';
import { currencyCode } from 'appConfig';
import { formatAmount, userNameSchema } from 'utils/helpers';
import { sendInvoice } from 'services/sendInvoice';
import { AppDispatch, RootState } from 'redux/store';
import Routes from 'constants/routes';

export const fieldWidth = { xs: '90%', lg: '70%', xl: '50%' };
export const buttonWidth = { xs: '45%', xl: '35%' };

const formSchema = yup.object({
    amount: yup
        .string()
        .required('Amount is required.')
        .test('amount', `Amount must be greater than $0.00 `, (value: any) => !(Number(value) < 0.01)),
    username: userNameSchema,
    subject: yup.string(),
    currencyCode: yup.string().required('Currency Code is required'),
});

export default function SendInvoiceForm() {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const defaultCompanyId = useSelector((state: RootState) => state.user.defaultCompanyId);
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const {
        watch,
        control,
        getValues,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(formSchema),
        defaultValues: {
            username: '',
            amount: '',
            subject: '',
            currencyCode,
        },
    });

    const confirmSendInvoice = async () => {
        const { amount, ...formValues } = getValues();
        const normalizedValues = { ...formValues, amount: Number(amount), companyCode: defaultCompanyId };
        console.log('normalizedValues', normalizedValues);
        setLoading(true);
        const { payload } = await dispatch(sendInvoice(normalizedValues));
        setLoading(false);
        if (payload?.code === 200) {
            // navigate(Routes.home.main)
        }
    };

    const handleFormSubmit = () => {
        setModalOpen(true);
    };

    return (
        <Card sx={{ p: { xs: 2, lg: 12 }, mx: { xs: 2, lg: 24 }, my: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontSize: 16, mb: 2, fontWeight: 400 }} variant="h1">
                Send Invoice
            </Typography>
            <Divider />
            <Typography sx={{ mt: 2, mb: 4 }} variant="body1">
                Send invoices to other users, organizations, or businesses. Select the recipient, the funding source, and the amount to
                send.
            </Typography>
            <form onSubmit={handleSubmit(handleFormSubmit)}>
                <Stack alignItems="center" gap={4}>
                    <Controller
                        name="username"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                icon={null}
                                sx={{ width: fieldWidth }}
                                placeholder="Type recipient"
                                label="Send To"
                                error={!!errors.username}
                                helperText={(errors?.username as any)?.message}
                            />
                        )}
                    />
                    {/* <Controller
            name='attentionTo'
            control={control}
            render={({field})=>(
              <TextInput
                  {...field}
                  icon={null}
                  sx={{width:{xs:'100%', sm:'50%'}}}
                  placeholder='Type recipient'
                  label='Send To'
              />
            )}
          /> */}
                    <TextInput
                        icon={null}
                        sx={{ width: fieldWidth }}
                        placeholder="Type here"
                        iconPosition="end"
                        label="Attention To"
                        disabled
                    />
                    <Box sx={{ width: fieldWidth }}>
                        <Selector disabled label="Deposit To" labelText="Parish balance - $1,635.00" />
                    </Box>
                    <Controller
                        name="amount"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                type="number"
                                icon={null}
                                sx={{ width: fieldWidth }}
                                placeholder="$0.00"
                                label="Amount"
                                error={!!errors.amount}
                                helperText={(errors?.amount as any)?.message}
                            />
                        )}
                    />
                    <Controller
                        name="subject"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                icon={null}
                                height={129}
                                sx={{ width: fieldWidth }}
                                placeholder="Type description here . . ."
                                label="Description"
                                minRows={4}
                                maxRows={4}
                                multiline
                            />
                        )}
                    />
                    <Button type="submit" sx={{ width: buttonWidth }} title="Send" />
                </Stack>
            </form>

            {/* Confirm Invoice Modal */}
            <Modal onClose={() => setModalOpen(false)} isOpen={modalOpen} modalTitle="Confirm Send Invoice">
                <>
                    <Typography sx={{ fontSize: { xs: 12, sm: 16 } }}>
                        Are you sure you want to send an invoice for <br />
                        <strong style={{ marginBottom: 2, fontSize: 20 }}>{formatAmount(watch('amount'))}</strong>
                    </Typography>
                    <Typography style={{ whiteSpace: 'nowrap' }}>
                        <p style={{ color: '#1F2937', marginTop: 3, marginBottom: 3 }}>To</p>
                        <strong style={{ fontSize: 20 }}>{watch('username')}</strong>
                    </Typography>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                        <Button
                            disabled={loading}
                            onClick={() => setModalOpen(false)}
                            width={'45%'}
                            sx={{ fontSize: { xs: 14, sm: 18 } }}
                            title="Cancel"
                        />
                        <Button
                            loading={loading}
                            onClick={confirmSendInvoice}
                            width={'45%'}
                            sx={{ fontSize: { xs: 14, sm: 18 } }}
                            title="Confirm"
                        />
                    </div>
                </>
            </Modal>
        </Card>
    );
}
